import { useMemo } from "react";

import { Classes, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Link } from "react-router";

import { pluralize } from "@/components/helpers/string-utils";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box, Flex } from "@/components/layout/flexbox";
import { DetailsBox } from "@/components/pages_flexible/coach/ProfileDetails";
import { CompanyLogo } from "@/components/pieces/companies/CompanyLogo";
import { DBFaqItems } from "@/components/pieces/faq/DBFaqItems";
import { Avatar } from "@/components/pieces/users/Avatar";
import { HEADING_FONT, SUBTEXT_COLOR } from "@/css/constants";
import {
  DetailGroupTypes,
  GetAgentProfileQuery,
  ServiceDetailQuery,
  useGetAgentProfileQuery,
} from "@/graphql";
import { NonNullableArrayItem } from "@/types/utils";

import { Subheading } from "./styles";

type CoachProduct = NonNullable<
  ServiceDetailQuery["coachBySlug"]
>["coachProduct"];
type Product = CoachProduct["product"];
type DetailGroupType = NonNullableArrayItem<Product["detailGroups"]>;
type DetailLineType = NonNullableArrayItem<Product["detailLines"]>;

type DetailGroupWithLines = DetailGroupType & {
  lines: DetailLineType[];
};

interface ServiceDetailsProps {
  product: Product;
}

function ServiceDetails({ product }: ServiceDetailsProps) {
  if (!product) {
    return null;
  }

  return (
    <Box mt={36} pt={36} className="bts">
      <Subheading>Service Details</Subheading>
      {product.duration ? (
        <Flex mt={24} alignItems="center">
          <Flex
            alignItems="center"
            justifyContent="center"
            backgroundColor="#E2EEFF"
            width={32}
            height={32}
            css={{ borderRadius: 100 }}
          >
            <Icon color="#587196" icon="time" size={16} />
          </Flex>
          <Box ml={2}>{product.duration} minutes</Box>
        </Flex>
      ) : null}
      {product.baseParticipants ? (
        <Flex mt={24} alignItems="center">
          <Flex
            alignItems="center"
            justifyContent="center"
            backgroundColor="#E2EEFF"
            width={32}
            height={32}
            css={{ borderRadius: 100 }}
          >
            <Icon color="#587196" icon="people" size={16} />
          </Flex>
          <Box ml={2}>
            {product.baseParticipants}
            {product.maxParticipants ? `-${product.maxParticipants}` : ""}{" "}
            participants
          </Box>
        </Flex>
      ) : null}
      {product.numRounds ? (
        <Flex mt={24} alignItems="center">
          <Flex
            alignItems="center"
            justifyContent="center"
            backgroundColor="#E2EEFF"
            width={32}
            height={32}
            css={{ borderRadius: 100 }}
          >
            <Icon color="#587196" icon="repeat" size={16} />
          </Flex>
          <Box ml={2}>
            {pluralize({
              amount: product.numRounds,
              singular: "round",
              plural: "rounds",
            })}
            of in-depth review
          </Box>
        </Flex>
      ) : null}
      {product.includesFinalCheck ? (
        <Flex mt={24} alignItems="center">
          <Flex
            alignItems="center"
            justifyContent="center"
            backgroundColor="#E2EEFF"
            width={32}
            height={32}
            css={{ borderRadius: 100 }}
          >
            <Icon color="#587196" icon="tick" size={16} />
          </Flex>
          <Box ml={2}>Includes final check</Box>
        </Flex>
      ) : null}
      {product.turnaroundTime ? (
        <Flex mt={24} alignItems="center">
          <Flex
            alignItems="center"
            justifyContent="center"
            backgroundColor="#E2EEFF"
            width={32}
            height={32}
            css={{ borderRadius: 100 }}
          >
            <Icon color="#587196" icon="time" size={16} />
          </Flex>
          <Box ml={2}>
            {product.turnaroundTime} business day turnaround time
          </Box>
        </Flex>
      ) : null}
    </Box>
  );
}

interface ProductDetailsProps {
  product: Product;
  coach?: ServiceDetailQuery["coachBySlug"];
}

export function ProductDetails({ product, coach }: ProductDetailsProps) {
  if (!product) {
    return null;
  }

  const isChatGPT = product?.slug === "chatgpt";
  const endorsements = product.endorsements ?? [];
  const faqAnswers = product.faqAnswers ?? [];

  const { data } = useGetAgentProfileQuery({
    variables: {
      userId: coach?.id,
    },
    skip: !coach,
  });

  const profile = data?.agentProfile;

  if (!profile) {
    return null;
  }

  return (
    <>
      {coach ? (
        <Box mb={36} pb={36} className="bbs">
          <Subheading>Provided By</Subheading>
          <Box mt={24}>
            <CoachOverview
              coach={coach}
              profile={profile}
              includeLink={!isChatGPT}
            />
          </Box>
        </Box>
      ) : null}
      <Box>
        <Subheading>Overview</Subheading>
        <Box mt={24}>
          <RichContentRenderer content={product.description} />
        </Box>
      </Box>
      {!isChatGPT && <ServiceDetails product={product} />}
      {!!endorsements.length && (
        <Flex alignItems="center" mt={36} pt={36} className="bts">
          <Box
            fontSize={13}
            fontWeight="bold"
            color={SUBTEXT_COLOR}
            css={{ letterSpacing: 1 }}
          >
            PREVIOUSLY DELIVERED TO
          </Box>
          {endorsements.map((endorsement) => {
            if (!endorsement.company) return null;
            return (
              <Box key={endorsement.id} ml={3}>
                <Tooltip
                  content={endorsement.company?.name}
                  key={endorsement.id}
                >
                  <CompanyLogo company={endorsement.company} width={40} />
                </Tooltip>
              </Box>
            );
          })}
        </Flex>
      )}
      <DetailGroups product={product} />
      {!!faqAnswers.length && (
        <Box mt={36} pt={36} className="bts">
          <Subheading>FAQs</Subheading>
          <Box mt={24}>
            <DBFaqItems faqItems={faqAnswers} />
          </Box>
        </Box>
      )}
      {coach ? (
        <Box mt={36} pt={36} className="bts">
          <Subheading>About {coach.name}</Subheading>
          <Box mt={24}>
            <CoachOverview
              coach={coach}
              profile={profile}
              includeLink={!isChatGPT}
            />
            <DetailsBox profile={profile} />
          </Box>
        </Box>
      ) : null}
    </>
  );
}

interface CoachOverviewProps {
  coach: ServiceDetailQuery["coachBySlug"];
  profile: GetAgentProfileQuery["agentProfile"];
  includeLink?: boolean;
}

function CoachOverview({
  coach,
  profile,
  includeLink = true,
}: CoachOverviewProps) {
  if (!coach) {
    return (
      <Flex alignItems="center">
        <Box
          height={120}
          width={120}
          className={Classes.SKELETON}
          style={{ borderRadius: 200 }}
        />
        <Box ml={3} flex={1}>
          <Box height={24} width={150} className={Classes.SKELETON} />
          <Box mt={1} height={48} className={Classes.SKELETON} />
        </Box>
      </Flex>
    );
  }
  return (
    <Flex alignItems="center">
      <Avatar size={120} user={coach} />
      <Box ml={3}>
        <Box fontSize={20} fontWeight={600}>
          {includeLink ? (
            <Link to={`/@${coach.slug}`}>{coach.name}</Link>
          ) : (
            coach.name
          )}
        </Box>
        <Box className={Classes.RUNNING_TEXT}>{profile?.summary}</Box>
      </Box>
    </Flex>
  );
}

interface DetailGroupsProps {
  product: Product;
}

function DetailGroups({ product }: DetailGroupsProps) {
  if (!product) {
    return null;
  }

  const detailLines = product.detailLines ?? [];
  // This aggregates the detail lines into an object that looks like:
  // {
  //   ids: [3, 4, 5],
  //   groups: {
  //     3: { ... },
  //     4: { ... },
  //     5: { ... }
  //   }
  // }
  // The goal is to render the detail groups in the order implied by
  // the order of the detail lines.
  const detailGroups: {
    ids: string[];
    groups: Record<string, DetailGroupWithLines>;
  } = useMemo(() => {
    if (!detailLines?.length) return { ids: [], groups: {} };
    return detailLines.reduce<{
      ids: string[];
      groups: Record<string, DetailGroupWithLines>;
    }>(
      (agg, line) => {
        const groupId = line.detailGroupId ?? "";
        if (!agg.groups[groupId]) {
          const detailGroup = product.detailGroups?.find(
            (dg) => dg?.id === groupId,
          );
          if (!detailGroup) return agg;
          agg.ids.push(groupId);
          agg.groups[groupId] = {
            ...detailGroup,
            lines: [line],
          };
        } else {
          agg.groups[groupId]?.lines.push(line);
        }
        return agg;
      },
      { ids: [], groups: {} },
    );
  }, [detailLines]);

  if (!detailLines?.length) return null;

  return (
    <>
      {detailGroups.ids.map((id) => (
        <Box key={id} mt={36} pt={36} className="bts">
          <Subheading>{detailGroups.groups[id].label}</Subheading>
          <Box mt={24}>
            <DetailGroup detailGroup={detailGroups.groups[id]} />
          </Box>
        </Box>
      ))}
    </>
  );
}

interface DetailGroupProps {
  detailGroup: DetailGroupWithLines;
}

function DetailGroup({ detailGroup }: DetailGroupProps) {
  if (detailGroup.displayType === DetailGroupTypes.Check) {
    return (
      <>
        {detailGroup.lines.map((line) => (
          <Flex key={line.id} mt={3} alignItems="top">
            <Flex
              alignItems="center"
              justifyContent="center"
              backgroundColor="#E2EEFF"
              minWidth={32}
              height={32}
              css={{ borderRadius: 100 }}
            >
              <Icon color="#587196" icon="tick" size={16} />
            </Flex>
            <Flex ml={3} alignItems="center">
              <DetailLine line={line} />
            </Flex>
          </Flex>
        ))}
      </>
    );
  } else if (detailGroup.displayType === DetailGroupTypes.Numbered) {
    return (
      <>
        {detailGroup.lines.map((line, i) => (
          <Flex key={line.id} mt={3} alignItems="top">
            <Flex
              alignItems="center"
              justifyContent="center"
              backgroundColor="#E2EEFF"
              color="#587196"
              fontFamily={HEADING_FONT}
              fontWeight={700}
              minWidth={32}
              height={32}
              css={{ borderRadius: 100 }}
            >
              {i + 1}
            </Flex>
            <Flex ml={3} alignItems="center">
              <DetailLine line={line} />
            </Flex>
          </Flex>
        ))}
      </>
    );
  } else if (detailGroup.displayType === DetailGroupTypes.Timeline) {
    return (
      <Box css={{ position: "relative" }}>
        {detailGroup.lines.map((line) => (
          <TimelineItem key={line.id}>
            <div className="timeline-line" />
            <div className="timeline-indicator" />
            <div className="timeline-content">
              <DetailLine line={line} />
            </div>
          </TimelineItem>
        ))}
      </Box>
    );
  }
  return null;
}

function DetailLine({ line }: { line: DetailLineType }) {
  return (
    <Box>
      {line.header ? (
        <Box mb={1} fontWeight={600} fontSize={18}>
          {line.header}
        </Box>
      ) : null}
      <Box>{line.text}</Box>
    </Box>
  );
}

const TimelineItem = styled("div")`
  position: relative;
  padding: 0 0 24px 32px;

  &:last-child {
    padding-bottom: 0;

    .timeline-line {
      display: none;
    }
  }
  .timeline-content {
    padding-top: 1px;
  }

  .timeline-line {
    position: absolute;
    top: 3px;
    left: 8px;
    width: 2px;
    height: 100%;
    border-left: 2px solid #e2eeff;
  }

  .timeline-indicator {
    width: 18px;
    height: 18px;
    border-radius: 100%;
    position: absolute;
    left: 0;
    top: 2px;
    background-color: #e2eeff;
  }
`;
